import { GraduationCap, Send, Store, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { apiErrorMessage } from '../../api/client'
import { adminApi } from '../../api/endpoints'
import { ErrorMessage } from '../ErrorMessage'

type Audience = 'providers' | 'students'
type Phase = 'choose' | 'running' | 'done'

const SLICE = 25
/** A safety net, not the normal end: 400 slices of 25 is 10,000 people in one run. */
const MAX_SLICES = 400

const AUDIENCES: { value: Audience; title: string; icon: typeof Store; blurb: string }[] = [
  {
    value: 'providers',
    title: 'Providers',
    icon: Store,
    blurb:
      'Tutors, centres, schools, colleges and companies that are not on the network yet. '
      + 'A provider with no category saved cannot be published - assign a category to them first.',
  },
  {
    value: 'students',
    title: 'Seekers',
    icon: GraduationCap,
    blurb:
      'Seekers who are not on the network yet and have a need, a location and a phone or '
      + 'email saved.',
  },
]

/**
 * Publishes people to the Signals-DPG network, for one audience at a time.
 *
 * <p>It asks first, because the two audiences are different decisions: providers are businesses
 * that want to be found, students are individuals whose details reach an outside network - and
 * children among them only by a deliberate choice, so that is its own option, off until ticked.
 * Anyone already on the network is left alone, so running it again is safe.
 */
export function PublishToNetworkDialog({ onClose }: { onClose: () => void }) {
  const [audience, setAudience] = useState<Audience>('providers')
  const [includeMinors, setIncludeMinors] = useState(false)
  const [phase, setPhase] = useState<Phase>('choose')
  const [published, setPublished] = useState(0)
  const [skipped, setSkipped] = useState(0)
  const [stopped, setStopped] = useState<'end' | 'cancelled' | 'limit' | 'error'>('end')
  const [error, setError] = useState<string | null>(null)
  const cancelled = useRef(false)

  async function run() {
    setPhase('running')
    setError(null)
    setPublished(0)
    setSkipped(0)
    cancelled.current = false
    let done = 0
    let notDone = 0
    let after: string | undefined
    let reason: 'end' | 'cancelled' | 'limit' | 'error' = 'limit'
    try {
      for (let slice = 0; slice < MAX_SLICES; slice++) {
        if (cancelled.current) {
          reason = 'cancelled'
          break
        }
        const res = await adminApi.signalsBackfill(
          audience === 'providers'
            ? { limit: SLICE, providersOnly: true, after }
            : { limit: SLICE, learnersOnly: true, adultsOnly: !includeMinors, after },
        )
        done += res.published
        notDone += res.skipped
        setPublished(done)
        setSkipped(notDone)
        // Fewer tried than asked for means that was the end of the list.
        if (!res.lastAttempted || res.published + res.skipped < SLICE) {
          reason = 'end'
          break
        }
        after = res.lastAttempted
      }
    } catch (err) {
      reason = 'error'
      setError(apiErrorMessage(err, 'Could not finish publishing to the network'))
    }
    setStopped(reason)
    setPhase('done')
  }

  const label = audience === 'providers' ? 'providers' : 'seekers'

  return (
    <div className="fixed inset-0 z-[1100] grid place-items-center bg-ink-900/40 p-4">
      <div className="card-pad max-h-[90vh] w-full max-w-lg overflow-y-auto bg-white">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-bold text-ink-900">
            <Send size={16} className="text-sun-500" /> Publish to the network
          </h2>
          {phase !== 'running' && (
            <button onClick={onClose} aria-label="Close" className="text-ink-400 hover:text-ink-700">
              <X size={17} />
            </button>
          )}
        </div>

        {phase === 'choose' && (
          <>
            <p className="mt-2 text-sm text-ink-600">
              Puts people on Signals-DPG so they can be found there. Choose who to publish - the two
              are separate, and anyone already published is left alone.
            </p>

            <div className="mt-4 space-y-2.5" role="radiogroup" aria-label="Who to publish">
              {AUDIENCES.map((a) => {
                const active = audience === a.value
                return (
                  <button
                    key={a.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setAudience(a.value)}
                    className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition ${
                      active ? 'border-sun-500 bg-sun-400/10' : 'border-ink-200 hover:bg-ink-50'
                    }`}
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink-100">
                      <a.icon size={17} className={active ? 'text-amber-700' : 'text-ink-500'} />
                    </span>
                    <span>
                      <span className="block font-semibold text-ink-900">{a.title}</span>
                      <span className="block text-xs text-ink-500">{a.blurb}</span>
                    </span>
                  </button>
                )
              })}
            </div>

            {audience === 'students' && (
              <label className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={includeMinors}
                  onChange={(e) => setIncludeMinors(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-semibold">Include seekers under 18.</span> Off by default:
                  a child&apos;s details reach an outside network only by a deliberate decision.
                  Leave it off to publish adults only.
                </span>
              </label>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="btn-ghost">Cancel</button>
              <button onClick={run} className="btn-sun">
                Publish {label}{audience === 'students' && !includeMinors ? ' (adults)' : ''}
              </button>
            </div>
          </>
        )}

        {phase === 'running' && (
          <>
            <p className="mt-3 text-sm text-ink-700">
              Publishing {label}... this takes about a minute for every 25 people.
            </p>
            <p className="mt-2 text-2xl font-bold text-ink-900">
              {published} <span className="text-sm font-medium text-ink-500">published</span>
            </p>
            {skipped > 0 && (
              <p className="text-sm text-ink-500">{skipped} could not be published so far.</p>
            )}
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => { cancelled.current = true }}
                className="btn-ghost"
              >
                Stop after this batch
              </button>
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            <p className="mt-3 text-2xl font-bold text-ink-900">
              {published} <span className="text-sm font-medium text-ink-500">{label} published</span>
            </p>
            {skipped > 0 && (
              <p className="mt-1 text-sm text-ink-600">
                {skipped} could not be published:{' '}
                {audience === 'providers'
                  ? 'no category saved, no location, or the network refused their details (a phone number it does not accept, or an account locked to an old domain).'
                  : 'no need saved, no location, no phone or email, or the network refused their details.'}
              </p>
            )}
            {stopped === 'cancelled' && (
              <p className="mt-1 text-sm text-ink-600">Stopped early. Open this again to carry on.</p>
            )}
            {stopped === 'limit' && (
              <p className="mt-1 text-sm text-ink-600">
                Stopped at the safety limit of {MAX_SLICES * SLICE} people. Open this again to carry on.
              </p>
            )}
            <ErrorMessage message={error} />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setPhase('choose')} className="btn-ghost">Publish another group</button>
              <button onClick={onClose} className="btn-sun">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
